/**
 * 📋 SDLC 6.5.0 UNIFIED SPECIFICATION STANDARD FILE HEADER
 * ===============================================================================
 * FILE: frontend/src/utils/attendancePhotoWatermark.ts
 * PURPOSE: Stamp employee name / GPS / address / device timestamp onto an
 *          attendance clock-in photo — web sync of mobile PR #603 watermark
 *          (BS-300 §1bis) for Route 2 (AttendanceModern), which had NO photo
 *          stamping at all (bare `<input type="file">`, base64'd unmodified).
 * SDLC FRAMEWORK VERSION: 6.5.0 (7-Pillar + 9 Mental Models + Long-Running Agent Protocol)
 * DATE: 2026-08-12
 * COMPLIANCE: SDLC 6.5.0 (mobile↔web behavior parity)
 * PILLAR: P1 (Stage 06 - BUILD)
 * SPRINT GOVERNANCE: Rule 2: Milestone-Gated Roadmaps
 *
 * @business-context
 * - Domain: Attendance evidence — audit trail parity between mobile and web
 * - Impact: Clock-in photos on web Route 2 now carry the same visible proof
 *   (name/GPS/address/time) mobile has shipped since PR #603.
 * - Stakeholders: Employees, Branch Managers (attendance dispute resolution)
 *
 * @technical-architecture
 * - Layer: Pure/near-pure utility (canvas + one network call — no React)
 * - Pattern: Extracted+generalized from the inline stamping algorithm ALREADY
 *   shipped and working on Route 1 (frontend/src/pages/dashboards/EmployeeDashboard.tsx,
 *   the native-file-input clock-in flow) — `buildWatermarkLines` /
 *   `drawWatermarkBar` are byte-for-byte the same layout math, split into
 *   testable pure functions. Route 1's inline implementation is left
 *   UNTOUCHED (already shipped, already proven — refactoring it in-place
 *   carries regression risk this task doesn't need to take); this module is
 *   net-new for Route 2's gap only.
 * - Mobile parity: mobile/src/screens/attendance/components/PhotoCapture.tsx
 *   watermarkBadge (name/GPS/address/timestamp bar) — web draws the same 4
 *   fields onto the canvas instead of an off-screen view-shot rig.
 *
 * @cross-references
 * - Reference impl (route 1, untouched): frontend/src/pages/dashboards/EmployeeDashboard.tsx
 * - Reverse-geocode proxy (existing, reused as-is): backend GET /api/v1/attendance/reverse-geocode
 * - Consumer: frontend/src/features/attendanceModern/useAttendanceModernClock.ts
 * - Tests: frontend/tests/utils/attendancePhotoWatermark.test.ts
 */

export interface WatermarkLineParams {
  employeeName: string;
  latitude: number | null;
  longitude: number | null;
  address: string;
  now: Date;
  /** Source image width in px — drives line-wrap width + font scaling, same as route 1. */
  imageWidthPx: number;
}

/**
 * Pure — builds the text lines shown in the bottom watermark bar (date/time,
 * name, GPS, wrapped address, Maps link). Extracted so it's unit-testable
 * without a canvas/Image.
 */
export function buildWatermarkLines(params: WatermarkLineParams): string[] {
  const { employeeName, latitude, longitude, address, now, imageWidthPx } = params;

  const dateStr = now.toLocaleDateString('vi-VN', { day: '2-digit', month: '2-digit', year: 'numeric' });
  const timeStr = now.toLocaleTimeString('vi-VN', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  const coordsStr = latitude != null && longitude != null ? `${latitude.toFixed(6)}, ${longitude.toFixed(6)}` : '';
  const mapsLink = latitude != null && longitude != null
    ? `maps.google.com/?q=${latitude.toFixed(6)},${longitude.toFixed(6)}`
    : '';

  // Smart-wrap long addresses at comma boundaries (same heuristic as route 1).
  const maxChars = Math.max(Math.floor(imageWidthPx / 16), 28);
  const addrLines: string[] = [];
  if (address) {
    let remaining = address;
    while (remaining.length > maxChars) {
      const cutIdx = remaining.lastIndexOf(', ', maxChars);
      if (cutIdx > 0) {
        addrLines.push(remaining.slice(0, cutIdx + 1).trim());
        remaining = remaining.slice(cutIdx + 2).trim();
      } else {
        addrLines.push(remaining.slice(0, maxChars));
        remaining = remaining.slice(maxChars).trim();
      }
    }
    if (remaining) addrLines.push(remaining);
  }

  return [
    `${dateStr}  ${timeStr}`,
    employeeName,
    coordsStr ? `GPS: ${coordsStr}` : null,
    ...addrLines,
    mapsLink || null,
  ].filter((line): line is string => Boolean(line));
}

/**
 * Reverse-geocode via the existing backend proxy (avoids CORS — same endpoint
 * route 1's inline flow already calls). Best-effort: returns '' on any
 * failure so the watermark still renders (name/GPS/timestamp) without an
 * address line, matching route 1's own fallback behavior.
 */
export async function reverseGeocode(
  lat: number,
  lon: number,
  accessToken: string,
  tenantKey: string,
): Promise<string> {
  try {
    const res = await fetch(`/api/v1/attendance/reverse-geocode?lat=${lat}&lon=${lon}`, {
      headers: { Authorization: `Bearer ${accessToken}`, 'X-Tenant-Key': tenantKey },
    });
    if (!res.ok) return '';
    const data = await res.json();
    return data?.address || '';
  } catch {
    return '';
  }
}

/**
 * Draws the bottom translucent-black watermark bar + white text onto a canvas
 * 2D context that already holds the source image. Same layout math as route 1.
 */
export function drawWatermarkBar(
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
  lines: string[],
): void {
  if (lines.length === 0) return;

  const barH = Math.max(height * 0.04 * lines.length, 70);
  ctx.fillStyle = 'rgba(0, 0, 0, 0.6)';
  ctx.fillRect(0, height - barH, width, barH);

  const fontSize = Math.max(Math.floor(width / 30), 13);
  ctx.font = `bold ${fontSize}px "Be Vietnam Pro", Arial, sans-serif`;
  ctx.fillStyle = '#ffffff';
  ctx.textAlign = 'left';
  const pad = Math.max(width * 0.03, 8);
  const lineH = fontSize * 1.25;
  const baseY = height - barH + lineH;
  lines.forEach((line, i) => {
    ctx.fillText(line, pad, baseY + i * lineH);
  });
}

export interface StampAttendancePhotoOptions {
  latitude: number | null;
  longitude: number | null;
  employeeName: string;
  accessToken: string;
  tenantKey: string;
  now?: Date;
  /** JPEG quality 0-1, default 0.85 (matches route 1). */
  quality?: number;
}

/**
 * Full pipeline: raw photo data-URL -> stamped data-URL. If canvas 2D context
 * is unavailable (should not happen in a real browser, but defends against
 * a headless/degraded environment) it resolves with the RAW photo rather than
 * failing the clock-in — watermarking is evidentiary enhancement, not a
 * blocking requirement (same non-blocking philosophy as G1/G2/G5).
 */
export function stampAttendancePhoto(
  rawPhotoDataUrl: string,
  opts: StampAttendancePhotoOptions,
): Promise<string> {
  const now = opts.now ?? new Date();

  return new Promise<string>((resolve, reject) => {
    const hasCoords = opts.latitude != null && opts.longitude != null;
    const addressPromise = hasCoords
      ? reverseGeocode(opts.latitude as number, opts.longitude as number, opts.accessToken, opts.tenantKey)
      : Promise.resolve('');

    addressPromise
      .then((address) => {
        const img = new Image();
        img.onload = () => {
          try {
            const canvas = document.createElement('canvas');
            canvas.width = img.width;
            canvas.height = img.height;
            const ctx = canvas.getContext('2d');
            if (!ctx) {
              resolve(rawPhotoDataUrl);
              return;
            }
            ctx.drawImage(img, 0, 0);
            const lines = buildWatermarkLines({
              employeeName: opts.employeeName,
              latitude: opts.latitude,
              longitude: opts.longitude,
              address,
              now,
              imageWidthPx: img.width,
            });
            drawWatermarkBar(ctx, canvas.width, canvas.height, lines);
            resolve(canvas.toDataURL('image/jpeg', opts.quality ?? 0.85));
          } catch (err) {
            reject(err);
          }
        };
        img.onerror = () => reject(new Error('watermark: failed to load raw photo into Image()'));
        img.src = rawPhotoDataUrl;
      })
      .catch(reject);
  });
}
